/* KinkKart nav v2 (2026-10-10): the collection filter bar (snippets/kk-brutal-filters).
   Every product is already on the page, each <li> carrying its tokens
   (data-kk="[for:her][heat:subtle][color:black]"), so filtering is instant:
   - a group matches if the product has any chosen value (OR), and a product
     shows if every group matches (AND);
   - each chip's count is what you'd get by adding it to the other groups'
     choices, and a chip that would empty the grid is disabled;
   - the sort reorders the grid in place;
   - choices are mirrored into the URL (?for=her&heat=subtle,suggestive&color=black&sort=newest)
     with replaceState, so a reload or a shared link keeps them and the back
     button still goes to the previous page. Menu links use the same params. */

class KkFilters extends HTMLElement {
  static HEAT = { subtle: 1, suggestive: 2, unmistakable: 3 };

  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    if (!this.grid) return;

    this.items = [...this.grid.querySelectorAll(':scope > [data-kk]')];
    this.items.forEach((li) => {
      li.kkTokens = KkFilters.parse(li.dataset.kk);
    });
    this.groups = [...this.querySelectorAll('[data-kk-group]')];
    this.state = Object.fromEntries(this.groups.map((group) => [group.dataset.kkGroup, new Set()]));
    this.sortSelect = this.querySelector('[data-kk-sort]');
    this.sort = 'featured';

    this.readUrl();

    this.addEventListener('click', (event) => {
      const chip = event.target.closest('.kk-chip');
      if (!chip || chip.disabled) return;
      const set = this.state[chip.closest('[data-kk-group]').dataset.kkGroup];
      const value = chip.dataset.kkValue;
      if (!value) set.clear();
      else if (set.has(value)) set.delete(value);
      else set.add(value);
      this.update();
    });
    document.querySelectorAll('[data-kk-clear]').forEach((button) =>
      button.addEventListener('click', () => {
        Object.values(this.state).forEach((set) => set.clear());
        this.update();
      })
    );
    this.sortSelect?.addEventListener('change', () => {
      this.sort = this.sortSelect.value;
      this.update();
    });

    this.update();
  }

  static parse(text) {
    const tokens = {};
    (text || '').replace(/\[([a-z]+):([^\]]+)\]/g, (match, key, value) => {
      (tokens[key] ||= []).push(value);
      return match;
    });
    return tokens;
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    Object.entries(this.state).forEach(([group, set]) => {
      const raw = params.get(group);
      if (!raw) return;
      const allowed = new Set(
        [...this.querySelectorAll(`[data-kk-group="${group}"] .kk-chip`)].map((chip) => chip.dataset.kkValue)
      );
      raw
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .forEach((value) => value && allowed.has(value) && set.add(value));
    });
    const sort = params.get('sort');
    if (this.sortSelect && sort && [...this.sortSelect.options].some((option) => option.value === sort)) {
      this.sort = sort;
    }
    if (this.sortSelect) this.sortSelect.value = this.sort;
  }

  matches(li, skipGroup) {
    return Object.entries(this.state).every(
      ([group, set]) => group === skipGroup || set.size === 0 || (li.kkTokens[group] || []).some((value) => set.has(value))
    );
  }

  update() {
    let shown = 0;
    this.items.forEach((li) => {
      const visible = this.matches(li);
      li.hidden = !visible;
      if (visible) shown += 1;
    });

    this.groups.forEach((groupEl) => {
      const group = groupEl.dataset.kkGroup;
      const set = this.state[group];
      groupEl.querySelectorAll('.kk-chip').forEach((chip) => {
        const value = chip.dataset.kkValue;
        if (!value) {
          chip.setAttribute('aria-pressed', String(set.size === 0));
          return;
        }
        const count = this.items.filter(
          (li) => this.matches(li, group) && (li.kkTokens[group] || []).includes(value)
        ).length;
        const on = set.has(value);
        chip.setAttribute('aria-pressed', String(on));
        chip.disabled = count === 0 && !on;
        const countEl = chip.querySelector('[data-kk-count]');
        if (countEl) countEl.textContent = count;
      });
    });

    const shownEl = this.querySelector('[data-kk-shown]');
    const nounEl = this.querySelector('[data-kk-noun]');
    if (shownEl) shownEl.textContent = shown;
    if (nounEl) nounEl.textContent = shown === 1 ? nounEl.dataset.one : nounEl.dataset.many;

    const active = Object.values(this.state).some((set) => set.size > 0);
    document.querySelectorAll('[data-kk-clear]').forEach((button) => {
      if (button.closest('[data-kk-empty]')) return;
      button.hidden = !active;
    });
    const empty = document.querySelector('[data-kk-empty]');
    if (empty) empty.hidden = shown !== 0;

    this.order();
    this.writeUrl();
  }

  order() {
    const key = {
      featured: (li) => Number(li.dataset.kkPos),
      newest: (li) => -Number(li.dataset.kkNew || 0),
      mild: (li) => KkFilters.HEAT[(li.kkTokens.heat || [])[0]] || 9,
      bold: (li) => -(KkFilters.HEAT[(li.kkTokens.heat || [])[0]] || 0),
      'price-asc': (li) => Number(li.dataset.kkPrice || 0),
      'price-desc': (li) => -Number(li.dataset.kkPrice || 0),
    }[this.sort];
    const byPosition = (a, b) => Number(a.dataset.kkPos) - Number(b.dataset.kkPos);
    const sorted = [...this.items].sort(
      this.sort === 'az'
        ? (a, b) => (a.dataset.kkTitle || '').localeCompare(b.dataset.kkTitle || '') || byPosition(a, b)
        : (a, b) => key(a) - key(b) || byPosition(a, b)
    );
    if (sorted.some((li, index) => this.grid.children[index] !== li)) this.grid.append(...sorted);
  }

  writeUrl() {
    const params = new URLSearchParams(window.location.search);
    Object.entries(this.state).forEach(([group, set]) => {
      if (set.size) params.set(group, [...set].join(','));
      else params.delete(group);
    });
    if (this.sort !== 'featured') params.set('sort', this.sort);
    else params.delete('sort');
    params.delete('page');
    const query = params.toString().replace(/%2C/gi, ',');
    const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(window.history.state, '', url);
    }
  }
}

customElements.define('kk-filters', KkFilters);
